// Copyright 2026 Hardfin, Inc.

/**
 * Company logo component module for displaying company logos using logo API.
 * Provides automatic logo lookup via domain, with fallback to manual uploads, initials, and company icon.
 */
import { Business } from '@/constants/hardfin-standard-icons';
import { getFileURL } from '@/file/utilities/get-download-url';
import { Avatar, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { decode } from 'blurhash';
import { useMemo, useState } from 'react';

/**
 * Logo.dev API key for client-side usage.
 * This is a publishable key designed for client-side use.
 */
const LOGO_DEV_API_KEY = 'pk_aaf1n3KYTyqmmkJ3UduJ8A';

/**
 * Size presets for the company logo component.
 */
type LogoSize = 'small' | 'medium';

/**
 * Size mappings in pixels for each size preset.
 */
const SIZE_MAP: Record<LogoSize, number> = {
  small: 32,
  medium: 48,
};

/**
 * Props for the CompanyLogo component
 */
interface Props {
  /**
   * The company website domain for logo lookup (e.g., "acme.com")
   * If provided, will attempt to fetch from logo API
   */
  domain: string | null;

  /**
   * The company name, used for tooltip and initials fallback
   */
  name: string;

  /**
   * The size preset for the logo
   * @default "small"
   */
  size?: LogoSize;

  /**
   * Optional manually uploaded logo file key
   * Takes precedence over logo API lookup when provided
   */
  manualLogoUrl?: string | null;

  /**
   * Optional blurhash string for placeholder image while loading
   * When provided, shows a blurred preview instead of skeleton
   */
  blurhash?: string | null;
}

/**
 * Decodes a blurhash string to a data URL for use as an image placeholder.
 * Returns null if the blurhash is invalid or decoding fails.
 *
 * @param blurhash - The blurhash string to decode
 * @param width - Width of the output image
 * @param height - Height of the output image
 * @returns Data URL string or null if decoding fails
 */
function decodeBlurhashToDataUrl(
  blurhash: string,
  width: number,
  height: number
): string | null {
  try {
    const pixels = decode(blurhash, width, height);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const imageData = ctx.createImageData(width, height);
    imageData.data.set(pixels);
    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL();
  } catch {
    return null;
  }
}

/**
 * Generates initials from a company name.
 * Returns the first two characters for multi-word names,
 * or the first character for single-word names.
 *
 * @param name - The company name
 * @returns The initials string (1-2 characters), or empty string if name is blank
 */
function getInitials(name: string): string {
  const trimmed = name.trim();
  if (trimmed === '') {
    return '';
  }
  const words = trimmed.split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return trimmed.substring(0, 1).toUpperCase();
}

/**
 * Generates the logo API URL for a given domain.
 *
 * @param domain - The company domain
 * @param size - The desired logo size in pixels
 * @returns The logo API URL
 */
function getLogoDevUrl(domain: string, size: number): string {
  return `https://img.logo.dev/${domain}?token=${LOGO_DEV_API_KEY}&size=${size}&format=png`;
}

/**
 * Generates the tooltip content for a company logo.
 * Shows the company name, and optionally the domain below it.
 *
 * @param name - The company name
 * @param domain - The company domain (optional)
 * @returns React node for the tooltip content
 */
function getTooltipContent(
  name: string,
  domain: string | null
): React.ReactNode {
  if (domain == null || domain.trim() === '') {
    return (
      <Typography variant="caption" fontWeight="bold">
        {name}
      </Typography>
    );
  }

  return (
    <Stack direction="column" spacing={0}>
      <Typography variant="caption" fontWeight="bold">
        {name}
      </Typography>
      <Typography variant="caption">{domain}</Typography>
    </Stack>
  );
}

/**
 * A component that displays a company logo with intelligent fallback handling.
 *
 * Logo resolution priority:
 * 1. Manual upload (manualLogoUrl) - highest priority
 * 2. Logo API lookup using domain
 * 3. Initials avatar from company name
 * 4. Generic company icon - final fallback when no name
 *
 * Features:
 * - Lazy loads all images for performance
 * - Silent fallback on logo API errors (no error states shown)
 * - Circular avatar with consistent sizing
 * - Tooltip showing company name
 * - Loading skeleton during image load
 *
 * @example
 * ```tsx
 * // With domain for logo API lookup
 * <CompanyLogo
 *   domain="acme.com"
 *   name="Acme Corporation"
 *   size="medium"
 * />
 *
 * // With manual logo taking precedence
 * <CompanyLogo
 *   domain="acme.com"
 *   name="Acme Corporation"
 *   manualLogoUrl="file_abc123"
 * />
 *
 * // Fallback to initials (no domain or manual logo)
 * <CompanyLogo
 *   domain={null}
 *   name="Acme Corporation"
 * />
 *
 * // Fallback to company icon (no name at all)
 * <CompanyLogo
 *   domain={null}
 *   name=""
 * />
 * ```
 */
export function CompanyLogo(props: Props) {
  // Track if the logo image failed to load
  const [imageError, setImageError] = useState(false);
  // Track if the image is currently loading
  const [isLoading, setIsLoading] = useState(
    Boolean(props.manualLogoUrl || props.domain)
  );

  const size = SIZE_MAP[props.size ?? 'small'];

  // Decode blurhash to data URL for placeholder (memoized to avoid re-decoding)
  const blurhashDataUrl = useMemo(() => {
    if (!props.blurhash) return null;
    return decodeBlurhashToDataUrl(props.blurhash, size, size);
  }, [props.blurhash, size]);
  const initials = getInitials(props.name);

  // Determine which logo source to use
  const hasManualLogo = props.manualLogoUrl != null;
  const hasDomain = props.domain != null && props.domain.trim() !== '';

  // If no logo source or image failed, show initials or company icon
  if ((!hasManualLogo && !hasDomain) || imageError) {
    // Show initials if we have a name, otherwise show company icon
    if (initials) {
      return (
        <Tooltip arrow title={getTooltipContent(props.name, props.domain)}>
          <Avatar
            sx={{
              width: size,
              height: size,
              backgroundColor: 'grey.400',
              color: 'white',
              fontSize: size * 0.4,
              fontWeight: 600,
            }}
          >
            {initials}
          </Avatar>
        </Tooltip>
      );
    }

    // No name - show generic company icon
    return (
      <Tooltip arrow title={getTooltipContent(props.name, props.domain)}>
        <Avatar
          sx={{
            width: size,
            height: size,
            backgroundColor: 'grey.300',
            color: 'grey.600',
          }}
        >
          <Business sx={{ fontSize: size * 0.6 }} />
        </Avatar>
      </Tooltip>
    );
  }

  // Determine the logo URL
  const logoUrl = hasManualLogo
    ? getFileURL(props.manualLogoUrl as string)
    : getLogoDevUrl(props.domain as string, size * 2); // Request 2x size for retina

  return (
    <Tooltip arrow title={getTooltipContent(props.name, props.domain)}>
      <div style={{ position: 'relative', width: size, height: size }}>
        {/* Loading placeholder - blurhash if available, otherwise skeleton */}
        {blurhashDataUrl ? (
          <Avatar
            src={blurhashDataUrl}
            sx={{
              width: size,
              height: size,
              position: 'absolute',
              visibility: isLoading ? 'visible' : 'hidden',
            }}
          />
        ) : (
          <Skeleton
            variant="circular"
            width={size}
            height={size}
            sx={{
              bgcolor: 'grey.200',
              position: 'absolute',
              visibility: isLoading ? 'visible' : 'hidden',
            }}
          />
        )}
        {/* Actual logo avatar */}
        <Avatar
          src={logoUrl}
          alt={`${props.name} logo`}
          sx={{
            border: 0,
            width: size,
            height: size,
            backgroundColor: 'white',
            visibility: isLoading ? 'hidden' : 'visible',
          }}
          slotProps={{
            img: {
              loading: 'lazy',
              onLoad: () => setIsLoading(false),
              onError: () => {
                setIsLoading(false);
                setImageError(true);
              },
            },
          }}
        />
      </div>
    </Tooltip>
  );
}
